"use client";

import { secToHourMinS } from "@/helpers/secToMinS";
import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import Vimeo from "@u-wave/react-vimeo";
import { getVimeoId } from "@/helpers/vimeoId";
import Prism from "prismjs";
import "prismjs/themes/prism-tomorrow.min.css";
import { useTranslations } from "next-intl";
import { useFreeOfferEnabled } from "@/contexts/CommerceSettingsContext";

export default function LookPart({ part, partdur }) {
    const t = useTranslations();
    // Offre gratuite coupée depuis /admin/promotions : plus de badge
    // « Gratuit » sur les séances, la porte étant fermée côté serveur.
    const freeOfferEnabled = useFreeOfferEnabled();
    const [open, setOpen] = useState(1 == part.number);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [selectedSession, setSelectedSession] = useState(null);
    const [videoId, setVideoId] = useState(null);
    const [time, setTime] = useState(0);
    const [timeOnTheVideo, setTimeOnTheVideo] = useState(0);
    const [videoIsPlaying, setVideoIsPlaying] = useState(false);

    const videoRef = useRef(null);

    const openPreview = (session) => {
        if (session.video) {
            setVideoId(getVimeoId(session.video));
        }
        setModalIsOpen(true);
        setSelectedSession(session);
        Prism.highlightAll();
    };
    const onVimeoIsPlaying = async (data) => {
        setTime(data.percent);

        // Increment time on the video
        setTimeOnTheVideo(timeOnTheVideo + 0.255);
    };

    useEffect(() => {
        if (modalIsOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
    }, [modalIsOpen]);

    return (
        <div
            key={part.number}
            className="dark:text-textSecondDark custom-details"
        >
            <AnimatePresence>
                {modalIsOpen && (
                    <div className="bg-grayDark-900/80 fixed top-0 left-0 bottom-0 right-0 flex justify-center overflow-y-auto items-start z-50">
                        <motion.div
                            variants={{
                                hidden: { opacity: 0, y: 15 },
                                visible: { opacity: 1, y: 0 },
                            }}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            className="flex lg:flex-row flex-col lg:w-[900px] w-full overflow-y-auto p-5"
                        >
                            <div className="flex-1 bg-white p-9 relative text-sm lg:order-2 order-1 overflow-y-auto rounded-xl shadow">
                                <div className="absolute top-5 right-5 z-10">
                                    <svg
                                        onClick={() => {
                                            setVideoIsPlaying(false);
                                            setModalIsOpen(false);
                                            setSelectedSession(null);
                                        }}
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="w-8 h-8 text-blue-500 cursor-pointer"
                                        viewBox="0 0 24 24"
                                    >
                                        <g fill="currentColor">
                                            <path d="M10.03 8.97a.75.75 0 0 0-1.06 1.06L10.94 12l-1.97 1.97a.75.75 0 1 0 1.06 1.06L12 13.06l1.97 1.97a.75.75 0 0 0 1.06-1.06L13.06 12l1.97-1.97a.75.75 0 1 0-1.06-1.06L12 10.94l-1.97-1.97Z"></path>
                                            <path
                                                fillRule="evenodd"
                                                d="M12 1.25C6.063 1.25 1.25 6.063 1.25 12S6.063 22.75 12 22.75S22.75 17.937 22.75 12S17.937 1.25 12 1.25ZM2.75 12a9.25 9.25 0 1 1 18.5 0a9.25 9.25 0 0 1-18.5 0Z"
                                                clipRule="evenodd"
                                            ></path>
                                        </g>
                                    </svg>
                                </div>

                                <h3 className="text-2xl font-bold mb-5">
                                    {selectedSession.title}
                                </h3>

                                {videoId && (
                                    <>
                                        <Vimeo
                                            video={videoId}
                                            showByline={false}
                                            color="8468e3"
                                            showTitle={false}
                                            showPortrait={false}
                                            ref={videoRef}
                                            speed
                                            className="w-full rounded-t-lg overflow-hidden"
                                            dnt
                                            responsive
                                            onTimeUpdate={(data) =>
                                                onVimeoIsPlaying(data)
                                            }
                                            onPlaying={() =>
                                                setVideoIsPlaying(true)
                                            }
                                            paused={!videoIsPlaying}
                                        />
                                        {/* Progression */}
                                        <div className="h-1 bg-gray-300 dark:bg-bgDark-300">
                                            <div
                                                className="bg-primary h-1"
                                                style={{
                                                    width: 100 * time + "%",
                                                }}
                                            ></div>
                                        </div>
                                    </>
                                )}

                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: selectedSession.content,
                                    }}
                                    className="dark:text-white content-on-body"
                                ></div>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
            <div
                className="bg-gray-100 dark:text-white dark:bg-bgDark-840 border dark:border-borderDark dark:hover:bg-bgDark-550 mt-1 p-3 cursor-pointer rounded"
                onClick={() => setOpen((prev) => !prev)}
            >
                <div className="flex items-center gap-1">
                    <div className="w-5 h-5 relative">
                        <motion.div
                            animate={{ rotate: open ? 90 : 0, type: "spring" }}
                            transition={{ duration: 0.09 }}
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-5 h-5 text-primary"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    fill="currentColor"
                                    d="M15.835 11.63L9.205 5.2C8.79 4.799 8 5.042 8 5.57v12.86c0 .528.79.771 1.205.37l6.63-6.43a.498.498 0 0 0 0-.74"
                                ></path>
                            </svg>
                        </motion.div>
                    </div>
                    <div className="flex-1 flex justify-between items-center">
                        <div>
                            {t("LookPart_title", {
                                number: part.number,
                            })}{" "}
                            {part.title}
                        </div>
                        <div className="text-gray-500 dark:text-lightBlue text-xs">
                            {partdur > 0 && <>{secToHourMinS(partdur)}</>}
                        </div>
                    </div>
                </div>
            </div>

            <motion.div
                className="border-b dark:border-borderDark"
                variants={{
                    hidden: {
                        opacity: 0,
                    },
                    visible: {
                        opacity: 1,
                    },
                }}
            >
                <AnimatePresence>
                    {open &&
                        part.sessions &&
                        part.sessions
                            .sort((a, b) => a.position - b.position)
                            .map(
                                (session, index) =>
                                    session.online && (
                                        <motion.div
                                            key={`session-${index}`}
                                            id={`session-${session.id}`}
                                            className={`px-3 py-2 group border-l border-r dark:border-borderDark dark:hover:text-white hover:bg-light-hover dark:hover:bg-bgDark-550 duration-150`}
                                            variants={{
                                                hidden: {
                                                    y: 10,
                                                    opacity: 0,
                                                    transition: {
                                                        delay: index * 0.1,
                                                    },
                                                },
                                                visible: {
                                                    y: 0,
                                                    opacity: 1,
                                                    transition: {
                                                        delay: index * 0.1,
                                                    },
                                                },
                                                exit: {
                                                    y: 0,
                                                    opacity: 0,
                                                },
                                            }}
                                            initial="hidden"
                                            animate="visible"
                                            exit="exit"
                                        >
                                            <div className="flex gap-1">
                                                <div className="text-black dark:text-white dark:group-hover:text-white flex justify-between items-center w-full">
                                                    <div className="flex items-center gap-1">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="1em"
                                                            height="1em"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <path
                                                                fill="currentColor"
                                                                fillRule="evenodd"
                                                                d="M8.512 4.43a.75.75 0 0 1 1.057.082l6 7a.75.75 0 0 1 0 .976l-6 7a.75.75 0 0 1-1.138-.976L14.012 12L8.431 5.488a.75.75 0 0 1 .08-1.057"
                                                                clipRule="evenodd"
                                                            ></path>
                                                        </svg>
                                                        {session.title}
                                                    </div>
                                                    {freeOfferEnabled && session.isFreeToAccess && (
                                                        <div
                                                            className="text-primary hover:text-primary-300 duration-150 text-xs cursor-pointer"
                                                            onClick={() =>
                                                                openPreview(
                                                                    session,
                                                                )
                                                            }
                                                        >
                                                            {t(
                                                                "LookPart_preview",
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* {session.duration &&
                                                session.duration > 0 ? (
                                                    <div
                                                        className={`text-xs flex items-center gap-1 text-gray-500 dark:text-lightBlue`}
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={1.5}
                                                            stroke="currentColor"
                                                            className="w-4 h-4"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z"
                                                            />
                                                        </svg>
                                                        {secToHourMinS(
                                                            session.duration,
                                                        )}
                                                    </div>
                                                ) : (
                                                    ""
                                                )} */}
                                                </div>
                                            </div>
                                        </motion.div>
                                    ),
                            )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
}
